'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { containsCatalogKeyword, normalizeCatalogText } from '@/lib/catalog/guards';
import { productImageOrFallback } from '@/lib/products/product-image';
import type { FilterSchemaGroup } from '@/lib/catalog/filters';
import { cpuFilterGroups } from '@/lib/cpu-filter-config';
import { mainboardFilterGroups } from '@/lib/mainboard-filter-config';
import { ramFilterGroups } from '@/lib/ram-filter-config';
import { ssdFilterGroups } from '@/lib/ssd-filter-config';
import { hddFilterGroups } from '@/lib/hdd-filter-config';
import { vgaFilterGroups } from '@/lib/vga-filter-config';
import { caseFilterGroups } from '@/lib/case-filter-config';
import { keyboardFilterGroups } from '@/lib/keyboard-filter-config';
import { mouseFilterGroups } from '@/lib/mouse-filter-config';
import { headsetFilterGroups } from '@/lib/headset-filter-config';
import { PC_SLOTS, SLOT_MAP } from '@/lib/pc-builder/constants';
import { evaluateCompatibility } from '@/lib/pc-builder/compatibility';
import type {
  PcBuilderProduct,
  PcSlotKey,
  ProductFacets,
  ProductQueryFilters,
} from '@/lib/pc-builder/types';

type ShopInfo = {
  name: string;
  email: string;
  phone: string;
  address: string;
  logoUrl: string;
};

type FetchResponse = {
  slot: PcSlotKey;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  facets: ProductFacets;
  items: PcBuilderProduct[];
};

type SlotFilterState = {
  q: string;
  brand: string;
  minPrice: string;
  maxPrice: string;
  socket: string;
  ramType: string;
  minWatt: string;
  minCapacity: string;
  maxCapacity: string;
  chipset: string;
  sort: ProductQueryFilters['sort'];
  page: number;
};

type ToastState = {
  tone: 'success' | 'error' | 'info';
  message: string;
};

type AiBuildResult = {
  summary: string;
  notes: string[];
  selections: Partial<Record<PcSlotKey, string>>;
  products: PcBuilderProduct[];
  total: number;
  budget: number;
};

const BUILD_STORAGE_KEY = 'dntech_pc_builder_config_v1';

const defaultFilters: SlotFilterState = {
  q: '',
  brand: '',
  minPrice: '',
  maxPrice: '',
  socket: '',
  ramType: '',
  minWatt: '',
  minCapacity: '',
  maxCapacity: '',
  chipset: '',
  sort: 'featured',
  page: 1,
};

const SLOT_FILTER_GROUPS: Partial<Record<PcSlotKey, FilterSchemaGroup[]>> = {
  cpu: cpuFilterGroups,
  mainboard: mainboardFilterGroups,
  ram: ramFilterGroups,
  ssd: ssdFilterGroups,
  hdd: hddFilterGroups,
  vga: vgaFilterGroups,
  case: caseFilterGroups,
  keyboard: keyboardFilterGroups,
  mouse: mouseFilterGroups,
  headset: headsetFilterGroups,
};

function formatPrice(value: number) {
  return `${new Intl.NumberFormat('vi-VN').format(Math.max(0, value || 0))}₫`;
}

function toPositiveInt(value?: number) {
  if (!value || value <= 0) return 1;
  return Math.max(1, Math.floor(value));
}

function escapeHtml(input: string) {
  return input
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function encodePayload(value: unknown) {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function decodePayload<T>(encoded: string): T | null {
  try {
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function imageSrc(product: PcBuilderProduct) {
  return productImageOrFallback(product.image);
}

function getPrice(product: PcBuilderProduct) {
  return Number(product.price || 0);
}

function getOriginalPrice(product: PcBuilderProduct) {
  const original = Number(product.original_price || 0);
  const price = getPrice(product);
  return original > 0 ? original : price;
}

function filterForSlot(
  slot: PcSlotKey,
  filtersBySlot: Partial<Record<PcSlotKey, SlotFilterState>>
) {
  return filtersBySlot[slot] || defaultFilters;
}

function buildProductParams(slot: PcSlotKey, filters: SlotFilterState) {
  const params = new URLSearchParams();
  params.set('slot', slot);
  params.set('page', String(filters.page));
  params.set('limit', '12');
  params.set('sort', filters.sort || 'featured');

  if (filters.q.trim()) params.set('q', filters.q.trim());
  if (filters.brand) params.set('brand', filters.brand);
  if (filters.minPrice) params.set('minPrice', filters.minPrice);
  if (filters.maxPrice) params.set('maxPrice', filters.maxPrice);
  if (filters.socket) params.set('socket', filters.socket);
  if (filters.ramType) params.set('ramType', filters.ramType);
  if (filters.minWatt) params.set('minWatt', filters.minWatt);
  if (filters.minCapacity) params.set('minCapacity', filters.minCapacity);
  if (filters.maxCapacity) params.set('maxCapacity', filters.maxCapacity);
  if (filters.chipset) params.set('chipset', filters.chipset);

  return params;
}

export default function PcBuilderClient({ shopInfo }: { shopInfo: ShopInfo }) {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const { addToCart } = useCart();

  const [activeSlot, setActiveSlot] = useState<PcSlotKey | null>(null);
  const [selected, setSelected] = useState<Partial<Record<PcSlotKey, PcBuilderProduct>>>({});
  const [quantities, setQuantities] = useState<Partial<Record<PcSlotKey, number>>>({});
  const [filtersBySlot, setFiltersBySlot] = useState<Partial<Record<PcSlotKey, SlotFilterState>>>(
    {}
  );
  const [slotSpecificFilters, setSlotSpecificFilters] = useState<
    Partial<Record<PcSlotKey, Record<string, string>>>
  >({});

  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState<FetchResponse | null>(null);
  const [modalError, setModalError] = useState('');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiBudget, setAiBudget] = useState('20000000');
  const [aiPurpose, setAiPurpose] = useState('gaming');
  const [aiResolution, setAiResolution] = useState('1080p');
  const [aiCpuBrand, setAiCpuBrand] = useState('không yêu cầu');
  const [aiPriorities, setAiPriorities] = useState('');
  const [aiIncludePeripherals, setAiIncludePeripherals] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiResult, setAiResult] = useState<AiBuildResult | null>(null);
  const productCacheRef = useRef<Map<string, FetchResponse>>(new Map());
  const prefetchingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash) {
      const payload = decodePayload<{
        selected: Partial<Record<PcSlotKey, PcBuilderProduct>>;
        quantities: Partial<Record<PcSlotKey, number>>;
      }>(decodeURIComponent(hash));
      if (payload?.selected) {
        setSelected(payload.selected);
        setQuantities(payload.quantities || {});
        setToast({ tone: 'info', message: 'Đã tải cấu hình từ liên kết chia sẻ.' });
        return;
      }
    }

    const saved = localStorage.getItem(BUILD_STORAGE_KEY);
    if (!saved) return;
    const payload = decodePayload<{
      selected: Partial<Record<PcSlotKey, PcBuilderProduct>>;
      quantities: Partial<Record<PcSlotKey, number>>;
    }>(saved);
    if (!payload?.selected) return;
    setSelected(payload.selected);
    setQuantities(payload.quantities || {});
  }, []);

  useEffect(() => {
    if (!activeSlot) return;

    const currentFilters = filterForSlot(activeSlot, filtersBySlot);
    const params = buildProductParams(activeSlot, currentFilters);
    const cacheKey = params.toString();
    const cached = productCacheRef.current.get(cacheKey);

    if (cached) {
      setModalData(cached);
      setModalLoading(false);
      setModalError('');
      return;
    }

    const controller = new AbortController();

    async function run() {
      setModalLoading(true);
      setModalError('');
      try {
        const res = await fetch(`/api/pc-builder/products?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('Không tải được dữ liệu linh kiện.');
        const data = (await res.json()) as FetchResponse;
        productCacheRef.current.set(cacheKey, data);
        setModalData(data);
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        setModalError((error as Error).message || 'Không tải được dữ liệu linh kiện.');
      } finally {
        setModalLoading(false);
      }
    }

    run();

    return () => controller.abort();
  }, [activeSlot, filtersBySlot]);

  const compatibility = useMemo(
    () => evaluateCompatibility(selected, quantities),
    [selected, quantities]
  );

  const selectedRows = useMemo(
    () =>
      PC_SLOTS.map((slot) => {
        const product = selected[slot.key] || null;
        const quantity = toPositiveInt(quantities[slot.key]);
        return { slot, product, quantity };
      }),
    [selected, quantities]
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let finalTotal = 0;

    for (const row of selectedRows) {
      if (!row.product) continue;
      subtotal += getOriginalPrice(row.product) * row.quantity;
      finalTotal += getPrice(row.product) * row.quantity;
    }

    return {
      subtotal,
      discount: Math.max(0, subtotal - finalTotal),
      finalTotal,
    };
  }, [selectedRows]);

  const missingRequiredSlots = useMemo(
    () =>
      selectedRows.filter((row) => row.slot.required && !row.product).map((row) => row.slot.label),
    [selectedRows]
  );

  function setFilters(slot: PcSlotKey, next: Partial<SlotFilterState>) {
    setFiltersBySlot((prev) => {
      const current = filterForSlot(slot, prev);
      return {
        ...prev,
        [slot]: {
          ...current,
          ...next,
        },
      };
    });
  }

  function setSlotSpecificFilter(slot: PcSlotKey, groupId: string, value: string) {
    setSlotSpecificFilters((prev) => ({
      ...prev,
      [slot]: {
        ...(prev[slot] || {}),
        [groupId]: value,
      },
    }));
  }

  function prefetchProducts(slot: PcSlotKey) {
    const nextFilters = {
      ...filterForSlot(slot, filtersBySlot),
      page: 1,
    };
    const params = buildProductParams(slot, nextFilters);
    const cacheKey = params.toString();

    if (productCacheRef.current.has(cacheKey) || prefetchingRef.current.has(cacheKey)) return;

    prefetchingRef.current.add(cacheKey);
    fetch(`/api/pc-builder/products?${cacheKey}`)
      .then((res) => {
        if (!res.ok) throw new Error('prefetch failed');
        return res.json() as Promise<FetchResponse>;
      })
      .then((data) => {
        productCacheRef.current.set(cacheKey, data);
      })
      .catch(() => {
        // Prefetch chỉ để tăng tốc mở modal, lỗi im lặng để không ảnh hưởng UX chính.
      })
      .finally(() => {
        prefetchingRef.current.delete(cacheKey);
      });
  }

  function openPicker(slot: PcSlotKey) {
    const nextFilters = {
      ...filterForSlot(slot, filtersBySlot),
      page: 1,
    };
    const cached = productCacheRef.current.get(buildProductParams(slot, nextFilters).toString());

    setActiveSlot(slot);
    if (cached) {
      setModalData(cached);
      setModalLoading(false);
    } else if (modalData?.slot !== slot) {
      setModalData(null);
    }
    setModalError('');
    setFilters(slot, { page: 1 });
  }

  function selectProduct(slot: PcSlotKey, product: PcBuilderProduct) {
    setSelected((prev) => ({ ...prev, [slot]: product }));
    setQuantities((prev) => ({
      ...prev,
      [slot]: SLOT_MAP[slot].allowQuantity ? toPositiveInt(prev[slot]) : 1,
    }));
    setToast({ tone: 'success', message: `Đã chọn ${SLOT_MAP[slot].label}: ${product.name}` });
    setActiveSlot(null);
  }

  function removeProduct(slot: PcSlotKey) {
    setSelected((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
    setQuantities((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
  }

  function changeQuantity(slot: PcSlotKey, nextQty: number) {
    const normalized = Math.max(1, Math.floor(nextQty || 1));
    setQuantities((prev) => ({ ...prev, [slot]: normalized }));
  }

  function saveBuild() {
    const payload = { selected, quantities, savedAt: new Date().toISOString() };
    localStorage.setItem(BUILD_STORAGE_KEY, encodePayload(payload));
    setToast({ tone: 'success', message: 'Đã lưu cấu hình trên thiết bị này.' });
  }

  function shareBuild() {
    const payload = { selected, quantities };
    const hash = encodeURIComponent(encodePayload(payload));
    const shareUrl = `${window.location.origin}/xay-dung-cau-hinh#${hash}`;

    navigator.clipboard
      .writeText(shareUrl)
      .then(() => setToast({ tone: 'success', message: 'Đã sao chép link cấu hình.' }))
      .catch(() =>
        setToast({ tone: 'error', message: 'Không thể sao chép link, vui lòng thử lại.' })
      );
  }

  function resetBuild() {
    setSelected({});
    setQuantities({});
    window.location.hash = '';
    setToast({ tone: 'info', message: 'Đã làm mới cấu hình.' });
  }

  function buildQuoteHtml(mode: 'print' | 'pdf') {
    const rows = selectedRows
      .filter((row) => row.product)
      .map((row, index) => {
        const product = row.product as PcBuilderProduct;
        const price = getPrice(product);
        const lineTotal = price * row.quantity;
        const status = product.stock > 0 ? 'Còn hàng' : 'Hết hàng';
        return `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(row.slot.label)}</td>
            <td>
              <div style="font-weight:700">${escapeHtml(product.name)}</div>
              <div style="font-size:12px;color:#6b7280">SKU: ${escapeHtml(product.sku || 'N/A')}</div>
            </td>
            <td style="text-align:center">${row.quantity}</td>
            <td style="text-align:right">${formatPrice(price)}</td>
            <td style="text-align:right">${formatPrice(lineTotal)}</td>
            <td style="text-align:center">${status}</td>
          </tr>
        `;
      })
      .join('');

    const issuesHtml = compatibility.issues
      .map((issue) => {
        const color =
          issue.severity === 'error'
            ? '#dc2626'
            : issue.severity === 'warning'
              ? '#d97706'
              : '#2563eb';
        return `<li style="margin-bottom:4px;color:${color}"><strong>${escapeHtml(issue.title)}:</strong> ${escapeHtml(issue.detail)}</li>`;
      })
      .join('');

    const contactLine = [shopInfo.address, shopInfo.phone, shopInfo.email]
      .filter(Boolean)
      .join(' | ');

    return `<!doctype html>
<html lang="vi">
  <head>
    <meta charset="utf-8" />
    <title>Bao gia cau hinh PC</title>
    <style>
      body { font-family: Arial, sans-serif; color: #111827; margin: 0; padding: 24px; background: #f8fafc; }
      .paper { max-width: 980px; margin: 0 auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 22px; }
      .head { display: flex; justify-content: space-between; gap: 16px; border-bottom: 2px solid #d1d5db; padding-bottom: 14px; margin-bottom: 16px; }
      .title { font-size: 24px; font-weight: 800; margin: 0 0 8px; }
      .muted { color: #6b7280; font-size: 12px; }
      .badge { display: inline-block; background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 700; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; }
      th, td { border: 1px solid #e5e7eb; padding: 9px; font-size: 13px; vertical-align: top; }
      th { background: #f3f4f6; text-transform: uppercase; font-size: 11px; letter-spacing: .04em; }
      .totals { margin-top: 14px; margin-left: auto; width: 360px; }
      .totals .row { display: flex; justify-content: space-between; margin: 6px 0; }
      .totals .grand { font-size: 20px; font-weight: 800; color: #dc2626; border-top: 1px solid #d1d5db; padding-top: 8px; }
      .issues { margin-top: 18px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 12px; }
      .footer { margin-top: 18px; font-size: 12px; color: #6b7280; }
      .logo { max-height: 42px; max-width: 140px; object-fit: contain; }
      @media print { body { background: #fff; padding: 0; } .paper { border: none; border-radius: 0; padding: 16px; } }
    </style>
  </head>
  <body>
    <div class="paper">
      <div class="head">
        <div>
          ${shopInfo.logoUrl ? `<img class="logo" src="${escapeHtml(shopInfo.logoUrl)}" alt="logo" />` : ''}
          <h1 class="title">BẢNG BÁO GIÁ CẤU HÌNH PC</h1>
          <div class="muted">${escapeHtml(shopInfo.name || 'DN TECH')}</div>
          <div class="muted">${escapeHtml(contactLine || 'Cập nhật thông tin liên hệ tại phần cài đặt cửa hàng')}</div>
        </div>
        <div style="text-align:right">
          <div class="badge">${mode === 'pdf' ? 'Xuất PDF' : 'In báo giá'}</div>
          <div class="muted" style="margin-top:8px">Ngày lập: ${new Date().toLocaleString('vi-VN')}</div>
          <div class="muted">Tải ước tính: ${compatibility.estimatedLoadW}W</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Nhóm linh kiện</th>
            <th>Sản phẩm</th>
            <th>SL</th>
            <th>Đơn giá</th>
            <th>Thành tiền</th>
            <th>Tồn kho</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="7" style="text-align:center;color:#6b7280">Chưa có linh kiện trong cấu hình.</td></tr>'}
        </tbody>
      </table>

      <div class="totals">
        <div class="row"><span>Tạm tính</span><strong>${formatPrice(totals.subtotal)}</strong></div>
        <div class="row"><span>Giảm giá</span><strong>${formatPrice(totals.discount)}</strong></div>
        <div class="row grand"><span>Tổng cuối cùng</span><span>${formatPrice(totals.finalTotal)}</span></div>
      </div>

      <div class="issues">
        <div style="font-weight:700;margin-bottom:8px">Kiểm tra tương thích:</div>
        <ul style="margin:0;padding-left:18px">
          ${issuesHtml || '<li style="color:#047857">Không có cảnh báo nghiêm trọng ở mức kiểm tra cơ bản.</li>'}
        </ul>
      </div>

      <div class="footer">
        Giá có thể thay đổi theo thời điểm. Báo giá này được tạo tự động từ hệ thống build PC.
      </div>
    </div>

  </body>
</html>`;
  }

  function printOrExport(mode: 'print' | 'pdf') {
    const html = buildQuoteHtml(mode);
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      window.setTimeout(() => {
        iframe.remove();
      }, 1200);
    };

    try {
      const frameDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!frameDoc || !iframe.contentWindow) {
        cleanup();
        setToast({ tone: 'error', message: 'Không thể mở trình in trên trình duyệt hiện tại.' });
        return;
      }

      frameDoc.open();
      frameDoc.write(html);
      frameDoc.close();

      const triggerPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          setToast({ tone: 'error', message: 'Không thể mở hộp thoại in, vui lòng thử lại.' });
        } finally {
          cleanup();
        }
      };

      if (frameDoc.readyState === 'complete') {
        window.setTimeout(triggerPrint, 120);
      } else {
        iframe.onload = () => window.setTimeout(triggerPrint, 120);
      }
    } catch {
      cleanup();
      setToast({ tone: 'error', message: 'Không thể tạo báo giá để in, vui lòng thử lại.' });
      return;
    }

    if (mode === 'pdf') {
      setToast({ tone: 'info', message: 'Mở hộp thoại in, chọn Save as PDF để xuất file.' });
    }
  }

  function buildAddToCartItem(product: PcBuilderProduct) {
    return {
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: getPrice(product),
      original_price: getOriginalPrice(product),
      image: imageSrc(product),
      stock: Math.max(0, Math.floor(Number(product.stock || 0))),
    };
  }

  function addAllToCart() {
    if (authLoading) return;
    if (!user) {
      setToast({
        tone: 'info',
        message: 'Vui lòng đăng nhập để thêm toàn bộ cấu hình vào giỏ hàng.',
      });
      router.push('/dang-nhap?redirect=%2Fxay-dung-cau-hinh');
      return;
    }

    const outOfStock: string[] = [];
    let addedCount = 0;

    for (const row of selectedRows) {
      if (!row.product) continue;
      if (row.product.stock <= 0) {
        outOfStock.push(row.product.name);
        continue;
      }
      const ok = addToCart(buildAddToCartItem(row.product), row.quantity);
      if (ok) addedCount += 1;
    }

    if (addedCount > 0 && outOfStock.length === 0) {
      setToast({ tone: 'success', message: `Đã thêm ${addedCount} linh kiện vào giỏ hàng.` });
      return;
    }

    if (addedCount > 0 && outOfStock.length > 0) {
      setToast({
        tone: 'info',
        message: `Đã thêm ${addedCount} linh kiện. ${outOfStock.length} sản phẩm đang hết hàng và đã được bỏ qua.`,
      });
      return;
    }

    if (!addedCount && outOfStock.length) {
      setToast({ tone: 'error', message: 'Các linh kiện đã chọn hiện đều hết hàng.' });
      return;
    }

    setToast({ tone: 'info', message: 'Bạn chưa chọn linh kiện để thêm vào giỏ.' });
  }

  async function requestAiBuild(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAiError('');
    setAiResult(null);

    const budget = Number(aiBudget.replace(/[^0-9]/g, ''));
    if (!Number.isFinite(budget) || budget < 1_000_000) {
      setAiError('Vui lòng nhập ngân sách từ 1.000.000đ.');
      return;
    }

    setAiLoading(true);
    try {
      const response = await fetch('/api/pc-builder/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          budget,
          purpose: aiPurpose,
          resolution: aiResolution,
          cpuBrand: aiCpuBrand,
          priorities: aiPriorities,
          includePeripherals: aiIncludePeripherals,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Không thể tạo cấu hình bằng AI.');
      setAiResult(payload as AiBuildResult);
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Không thể kết nối AI lúc này.');
    } finally {
      setAiLoading(false);
    }
  }

  function applyAiBuild() {
    if (!aiResult) return;

    const productById = new Map(aiResult.products.map((product) => [product.id, product]));
    const nextSelected = { ...selected };
    const nextQuantities = { ...quantities };

    for (const [slot, productId] of Object.entries(aiResult.selections)) {
      if (!productId) continue;
      const slotKey = slot as PcSlotKey;
      const product = productById.get(productId);
      if (!product || product.stock <= 0 || !SLOT_MAP[slotKey]) continue;
      nextSelected[slotKey] = product;
      nextQuantities[slotKey] = 1;
    }

    setSelected(nextSelected);
    setQuantities(nextQuantities);
    setAiModalOpen(false);
    setAiResult(null);
    setToast({ tone: 'success', message: 'AI đã chọn và áp dụng cấu hình từ sản phẩm còn hàng.' });
  }

  const activeFilters = activeSlot ? filterForSlot(activeSlot, filtersBySlot) : defaultFilters;
  const activeSlotFilterGroups = activeSlot ? SLOT_FILTER_GROUPS[activeSlot] || [] : [];
  const activeSlotSpecificFilterValues = activeSlot ? slotSpecificFilters[activeSlot] || {} : {};

  const filteredModalItems = useMemo(() => {
    const baseItems = modalData?.items || [];
    if (!activeSlot) return baseItems;

    const groups = SLOT_FILTER_GROUPS[activeSlot] || [];
    if (!groups.length) return baseItems;

    const selectedGroups = groups
      .map((group) => ({
        group,
        value: (slotSpecificFilters[activeSlot] || {})[group.id] || '',
      }))
      .filter((entry) => Boolean(entry.value));

    if (!selectedGroups.length) return baseItems;

    return baseItems.filter((item) => {
      const haystack = normalizeCatalogText(
        [
          item.name,
          item.specs || '',
          item.sku || '',
          item.brand_name || '',
          item.category_name || '',
          item.category_slug || '',
          (item.tags || []).join(' '),
          JSON.stringify(item.full_specs || {}),
        ].join(' ')
      );

      return selectedGroups.every(({ group, value }) => {
        const option = group.options.find((opt) => opt.value === value);
        if (!option) return true;
        const keywords = option.keywords?.length ? option.keywords : [option.label];
        return keywords.some((keyword) => containsCatalogKeyword(haystack, keyword));
      });
    });
  }, [modalData?.items, activeSlot, slotSpecificFilters]);

  return (
    <div className="site-shell min-h-screen flex flex-col bg-[radial-gradient(circle_at_top,#fef3c7_0%,#f6f7fb_45%,#eef2ff_100%)]">
      <Header />

      <main className="mx-auto w-full max-w-[1380px] px-3 pb-10 pt-5 sm:px-4 lg:px-6">
        <nav className="mb-4 flex items-center gap-2 text-sm text-slate-500">
          <Link href="/" className="hover:text-slate-700">
            Trang chủ
          </Link>
          <span>/</span>
          <span className="font-semibold text-slate-700">Xây dựng cấu hình PC</span>
        </nav>

        <section className="mb-5 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-[0_14px_34px_rgba(15,23,42,0.09)] sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-[24px] font-extrabold tracking-tight text-slate-900 sm:text-[30px]">
                Build PC chuyên nghiệp
              </h1>
              <p className="mt-2 max-w-[900px] text-sm text-slate-600 sm:text-[15px]">
                Chọn linh kiện theo dữ liệu sản phẩm thật từ hệ thống, kiểm tra tương thích cơ bản
                theo thời gian thực, lên báo giá nhanh và thêm toàn bộ vào giỏ chỉ với một lần bấm.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setActiveSlot(null);
                  setAiError('');
                  setAiResult(null);
                  setAiModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white shadow-sm hover:bg-indigo-700"
              >
                <SparklesIcon className="h-4 w-4" />
                AI hỗ trợ build
              </button>
              <button
                type="button"
                onClick={saveBuild}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Lưu cấu hình
              </button>
              <button
                type="button"
                onClick={shareBuild}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Chia sẻ link cấu hình
              </button>
              <button
                type="button"
                onClick={resetBuild}
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
              >
                Làm mới cấu hình
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <aside className="xl:col-span-2">
            <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm">
              <div className="mb-3 text-sm font-extrabold uppercase tracking-wide text-slate-700">
                Nhóm linh kiện
              </div>
              <div className="space-y-2">
                {selectedRows.map((row) => {
                  const hasValue = Boolean(row.product);
                  return (
                    <button
                      key={row.slot.key}
                      type="button"
                      onFocus={() => prefetchProducts(row.slot.key)}
                      onMouseEnter={() => prefetchProducts(row.slot.key)}
                      onClick={() => openPicker(row.slot.key)}
                      className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition ${
                        hasValue
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wide">
                        {row.slot.label}
                      </span>
                      <span className="text-[11px] font-bold">{hasValue ? 'Đã chọn' : 'Chọn'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <div className="xl:col-span-6 space-y-3">
            {selectedRows.map((row) => {
              const slotHasIssues = compatibility.issues.some((issue) =>
                issue.slots.includes(row.slot.key)
              );
              return (
                <div
                  key={row.slot.key}
                  className={`rounded-2xl border p-3 sm:p-4 ${
                    row.product
                      ? 'border-slate-200 bg-white/95 shadow-sm'
                      : 'border-dashed border-slate-300 bg-white/70'
                  } ${slotHasIssues ? 'ring-1 ring-amber-300' : ''}`}
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
                        {row.slot.required ? 'Bắt buộc' : 'Tùy chọn'}
                      </div>
                      <h3 className="text-base font-extrabold text-slate-900">{row.slot.label}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onFocus={() => prefetchProducts(row.slot.key)}
                        onMouseEnter={() => prefetchProducts(row.slot.key)}
                        onClick={() => openPicker(row.slot.key)}
                        className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-100"
                      >
                        {row.product ? 'Thay đổi' : 'Chọn linh kiện'}
                      </button>
                      {row.product ? (
                        <button
                          type="button"
                          onClick={() => removeProduct(row.slot.key)}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100"
                        >
                          Xóa
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {!row.product ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-600">
                      Chưa chọn {row.slot.label.toLowerCase()}. Bấm <b>Chọn linh kiện</b> để bắt
                      đầu.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[96px_1fr]">
                      <div className="h-24 w-24 overflow-hidden rounded-lg border border-slate-200 bg-white">
                        <Image
                          src={imageSrc(row.product)}
                          alt={row.product.name}
                          width={96}
                          height={96}
                          unoptimized
                          className="h-full w-full object-contain p-1 mix-blend-multiply"
                        />
                      </div>

                      <div>
                        <Link
                          href={`/san-pham/${row.product.slug}`}
                          className="line-clamp-2 text-sm font-bold text-slate-900 hover:text-blue-700"
                        >
                          {row.product.name}
                        </Link>

                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                          <span>SKU: {row.product.sku || 'N/A'}</span>
                          <span>|</span>
                          <span>{row.product.brand_name || 'Đang cập nhật hãng'}</span>
                          <span>|</span>
                          <span
                            className={row.product.stock > 0 ? 'text-emerald-700' : 'text-red-700'}
                          >
                            {row.product.stock > 0 ? `Còn ${row.product.stock}` : 'Hết hàng'}
                          </span>
                        </div>

                        <div className="mt-2 text-xs text-slate-600 line-clamp-2">
                          {row.product.specs || 'Thông số chi tiết đang được cập nhật.'}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-base font-extrabold text-red-600">
                            {formatPrice(getPrice(row.product))}
                          </span>
                          {getOriginalPrice(row.product) > getPrice(row.product) ? (
                            <span className="text-xs text-slate-400 line-through">
                              {formatPrice(getOriginalPrice(row.product))}
                            </span>
                          ) : null}

                          {SLOT_MAP[row.slot.key].allowQuantity ? (
                            <div className="ml-auto flex items-center overflow-hidden rounded-md border border-slate-300">
                              <button
                                type="button"
                                onClick={() => changeQuantity(row.slot.key, row.quantity - 1)}
                                className="px-2 py-1 text-sm text-slate-700 hover:bg-slate-100"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min={1}
                                value={row.quantity}
                                onChange={(event) =>
                                  changeQuantity(row.slot.key, Number(event.target.value || 1))
                                }
                                className="w-12 border-x border-slate-300 bg-white text-center text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => changeQuantity(row.slot.key, row.quantity + 1)}
                                className="px-2 py-1 text-sm text-slate-700 hover:bg-slate-100"
                              >
                                +
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <aside className="xl:col-span-4">
            <div className="sticky top-24 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm">
                <h2 className="text-lg font-extrabold text-slate-900">Tóm tắt cấu hình</h2>

                <div className="mt-3 space-y-2">
                  {selectedRows.map((row) => (
                    <div
                      key={row.slot.key}
                      className="flex items-start justify-between gap-3 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-slate-700">
                          {row.slot.label}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {row.product ? row.product.name : 'Chưa chọn'}
                        </div>
                      </div>
                      <div className="shrink-0 pl-2 text-right">
                        {row.product ? (
                          <>
                            <div className="whitespace-nowrap font-bold text-slate-900">
                              {formatPrice(getPrice(row.product) * row.quantity)}
                            </div>
                            {row.quantity > 1 ? (
                              <div className="text-[11px] text-slate-500">x{row.quantity}</div>
                            ) : null}
                          </>
                        ) : (
                          <button
                            type="button"
                            onFocus={() => prefetchProducts(row.slot.key)}
                            onMouseEnter={() => prefetchProducts(row.slot.key)}
                            onClick={() => openPicker(row.slot.key)}
                            className="whitespace-nowrap text-xs font-bold text-blue-700 hover:underline"
                          >
                            Chọn
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 space-y-2 border-t border-slate-200 pt-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tạm tính</span>
                    <span className="font-semibold text-slate-800">
                      {formatPrice(totals.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Giảm giá</span>
                    <span className="font-semibold text-emerald-700">
                      -{formatPrice(totals.discount)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="text-base font-extrabold text-slate-900">Tổng cuối</span>
                    <span className="text-xl font-extrabold text-red-600">
                      {formatPrice(totals.finalTotal)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    Tải hệ thống ước tính: {compatibility.estimatedLoadW}W
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => printOrExport('print')}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    In cấu hình
                  </button>
                  <button
                    type="button"
                    onClick={() => printOrExport('pdf')}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Xuất PDF báo giá
                  </button>
                  <button
                    type="button"
                    onClick={addAllToCart}
                    className="sm:col-span-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-bold text-white hover:bg-blue-800"
                  >
                    Thêm toàn bộ vào giỏ hàng
                  </button>
                </div>

                <div className="mt-2 text-xs text-slate-500">
                  Hệ thống giữ nguyên giỏ hàng hiện tại, chỉ cộng thêm các linh kiện còn hàng.
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm">
                <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-700">
                  Cảnh báo tương thích
                </h3>
                <div className="mt-3 space-y-2">
                  {compatibility.issues.length === 0 ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      Không phát hiện xung đột nghiêm trọng ở mức kiểm tra cơ bản.
                    </div>
                  ) : (
                    compatibility.issues.map((issue) => (
                      <div
                        key={issue.id}
                        className={`rounded-lg border px-3 py-2 text-sm ${
                          issue.severity === 'error'
                            ? 'border-red-200 bg-red-50 text-red-700'
                            : issue.severity === 'warning'
                              ? 'border-amber-200 bg-amber-50 text-amber-700'
                              : 'border-blue-200 bg-blue-50 text-blue-700'
                        }`}
                      >
                        <div className="font-bold">{issue.title}</div>
                        <div className="mt-1 text-xs">{issue.detail}</div>
                      </div>
                    ))
                  )}
                </div>

                {missingRequiredSlots.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
                    Thiếu linh kiện quan trọng: {missingRequiredSlots.join(', ')}.
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        </section>
      </main>

      {aiModalOpen ? (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-950/60 p-3 sm:p-6">
          <div className="flex max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-indigo-100 bg-indigo-600 px-5 py-4 text-white sm:px-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                  <SparklesIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-extrabold sm:text-xl">AI hỗ trợ build cấu hình</h2>
                  <p className="mt-0.5 text-xs text-indigo-100">
                    Chọn sản phẩm phù hợp từ kho đang còn hàng
                  </p>
                </div>
              </div>
              <button
                type="button"
                aria-label="Đóng AI hỗ trợ build"
                onClick={() => setAiModalOpen(false)}
                className="rounded-full p-2 text-white/80 hover:bg-white/15 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <form onSubmit={requestAiBuild} className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Ngân sách tối đa (đồng)
                    <input
                      type="text"
                      inputMode="numeric"
                      value={aiBudget}
                      onChange={(event) => setAiBudget(event.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="Ví dụ: 20000000"
                      className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 font-normal outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
                    />
                  </label>

                  <label className="block text-sm font-semibold text-slate-700">
                    Nhu cầu sử dụng
                    <select
                      value={aiPurpose}
                      onChange={(event) => setAiPurpose(event.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
                    >
                      <option value="gaming">Gaming</option>
                      <option value="van phong">Văn phòng</option>
                      <option value="do hoa va edit video">Đồ họa và edit video</option>
                      <option value="streaming">Streaming</option>
                      <option value="AI va render">AI và render</option>
                    </select>
                  </label>

                  <label className="block text-sm font-semibold text-slate-700">
                    Độ phân giải chơi game
                    <select
                      value={aiResolution}
                      onChange={(event) => setAiResolution(event.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
                    >
                      <option value="không chơi game">Không chơi game</option>
                      <option value="1080p">1080p</option>
                      <option value="1440p (2K)">1440p (2K)</option>
                      <option value="2160p (4K)">2160p (4K)</option>
                    </select>
                  </label>

                  <label className="block text-sm font-semibold text-slate-700">
                    Ưu tiên CPU
                    <select
                      value={aiCpuBrand}
                      onChange={(event) => setAiCpuBrand(event.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
                    >
                      <option value="không yêu cầu">Không yêu cầu</option>
                      <option value="Intel">Intel</option>
                      <option value="AMD">AMD</option>
                    </select>
                  </label>
                </div>

                <label className="block text-sm font-semibold text-slate-700">
                  Ưu tiên khác (không bắt buộc)
                  <textarea
                    value={aiPriorities}
                    onChange={(event) => setAiPriorities(event.target.value)}
                    rows={2}
                    placeholder="Ví dụ: máy chạy êm, dễ nâng cấp, ưu tiên SSD 1TB"
                    className="mt-1.5 w-full resize-none rounded-lg border border-slate-300 px-3 py-2.5 font-normal outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
                  />
                </label>

                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={aiIncludePeripherals}
                    onChange={(event) => setAiIncludePeripherals(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Chọn thêm màn hình, bàn phím, chuột và tai nghe
                </label>

                {aiError ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {aiError}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={aiLoading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60"
                >
                  <SparklesIcon className="h-5 w-5" />
                  {aiLoading ? 'AI đang phân tích sản phẩm trong kho...' : 'Tạo cấu hình bằng AI'}
                </button>
              </form>

              {aiResult ? (
                <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-sm font-extrabold uppercase tracking-wide text-emerald-800">
                        Gợi ý từ AI
                      </h3>
                      <p className="mt-1 text-sm text-slate-700">{aiResult.summary}</p>
                    </div>
                    <div className="shrink-0 text-left sm:text-right">
                      <div className="text-xs text-slate-500">Tổng tham khảo</div>
                      <div className="text-lg font-extrabold text-red-600">
                        {formatPrice(aiResult.total)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 divide-y divide-emerald-200 rounded-lg border border-emerald-200 bg-white">
                    {Object.entries(aiResult.selections).map(([slot, productId]) => {
                      const product = aiResult.products.find((item) => item.id === productId);
                      if (!product) return null;
                      return (
                        <div key={slot} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                          <span className="font-semibold text-slate-600">
                            {SLOT_MAP[slot as PcSlotKey]?.label || slot}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-right font-bold text-slate-900">
                            {product.name}
                          </span>
                          <span className="shrink-0 text-xs font-bold text-red-600">
                            {formatPrice(getPrice(product))}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {aiResult.notes.length > 0 ? (
                    <ul className="mt-3 space-y-1 text-xs text-slate-600">
                      {aiResult.notes.map((note) => (
                        <li key={note} className="flex gap-1.5">
                          <span className="text-emerald-700">•</span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <button
                    type="button"
                    onClick={applyAiBuild}
                    className="mt-4 w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700"
                  >
                    Áp dụng cấu hình này vào bảng build
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {activeSlot ? (
        <div className="fixed inset-0 z-[1200] flex bg-slate-900/50 backdrop-blur-[1px]">
          <div className="ml-auto h-full w-full max-w-[1020px] overflow-y-auto bg-white p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                  Chọn linh kiện
                </div>
                <h3 className="text-lg font-extrabold text-slate-900">
                  {SLOT_MAP[activeSlot].label}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSlot(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Đóng
              </button>
            </div>

            <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
                <input
                  value={activeFilters.q}
                  onChange={(event) => setFilters(activeSlot, { q: event.target.value, page: 1 })}
                  placeholder="Tìm theo tên sản phẩm"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />

                <select
                  value={activeFilters.brand}
                  onChange={(event) =>
                    setFilters(activeSlot, { brand: event.target.value, page: 1 })
                  }
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Tất cả hãng</option>
                  {(modalData?.facets.brands || []).map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>

                <input
                  value={activeFilters.minPrice}
                  onChange={(event) =>
                    setFilters(activeSlot, {
                      minPrice: event.target.value.replace(/[^0-9]/g, ''),
                      page: 1,
                    })
                  }
                  placeholder="Giá từ"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />

                <input
                  value={activeFilters.maxPrice}
                  onChange={(event) =>
                    setFilters(activeSlot, {
                      maxPrice: event.target.value.replace(/[^0-9]/g, ''),
                      page: 1,
                    })
                  }
                  placeholder="Giá đến"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />

                <select
                  value={activeFilters.socket}
                  onChange={(event) =>
                    setFilters(activeSlot, { socket: event.target.value, page: 1 })
                  }
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Socket</option>
                  {(modalData?.facets.sockets || []).map((socket) => (
                    <option key={socket} value={socket}>
                      {socket}
                    </option>
                  ))}
                </select>

                <select
                  value={activeFilters.ramType}
                  onChange={(event) =>
                    setFilters(activeSlot, { ramType: event.target.value, page: 1 })
                  }
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Chuẩn RAM</option>
                  {(modalData?.facets.ramTypes || []).map((ramType) => (
                    <option key={ramType} value={ramType}>
                      {ramType}
                    </option>
                  ))}
                </select>

                <input
                  value={activeFilters.minWatt}
                  onChange={(event) =>
                    setFilters(activeSlot, {
                      minWatt: event.target.value.replace(/[^0-9]/g, ''),
                      page: 1,
                    })
                  }
                  placeholder="PSU tối thiểu (W)"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />

                <input
                  value={activeFilters.minCapacity}
                  onChange={(event) =>
                    setFilters(activeSlot, {
                      minCapacity: event.target.value.replace(/[^0-9]/g, ''),
                      page: 1,
                    })
                  }
                  placeholder="Dung lượng từ (GB)"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />

                <input
                  value={activeFilters.maxCapacity}
                  onChange={(event) =>
                    setFilters(activeSlot, {
                      maxCapacity: event.target.value.replace(/[^0-9]/g, ''),
                      page: 1,
                    })
                  }
                  placeholder="Dung lượng đến (GB)"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />

                <select
                  value={activeFilters.chipset}
                  onChange={(event) =>
                    setFilters(activeSlot, { chipset: event.target.value, page: 1 })
                  }
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Chipset / Series</option>
                  {(modalData?.facets.chipsets || []).map((chipset) => (
                    <option key={chipset} value={chipset}>
                      {chipset}
                    </option>
                  ))}
                </select>

                <select
                  value={activeFilters.sort}
                  onChange={(event) =>
                    setFilters(activeSlot, {
                      sort: event.target.value as ProductQueryFilters['sort'],
                      page: 1,
                    })
                  }
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="featured">Ưu tiên phù hợp</option>
                  <option value="price-asc">Giá tăng dần</option>
                  <option value="price-desc">Giá giảm dần</option>
                  <option value="latest">Mới cập nhật</option>
                </select>

                <button
                  type="button"
                  onClick={() => {
                    setFilters(activeSlot, {
                      ...defaultFilters,
                      page: 1,
                    });
                    setSlotSpecificFilters((prev) => ({ ...prev, [activeSlot]: {} }));
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Xóa bộ lọc
                </button>
              </div>

              {activeSlotFilterGroups.length > 0 ? (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <div className="mb-2 text-xs font-extrabold uppercase tracking-[0.08em] text-amber-800">
                    Bộ lọc riêng: {SLOT_MAP[activeSlot].label}
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {activeSlotFilterGroups.map((group) => (
                      <select
                        key={group.id}
                        value={activeSlotSpecificFilterValues[group.id] || ''}
                        onChange={(event) =>
                          setSlotSpecificFilter(activeSlot, group.id, event.target.value)
                        }
                        className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">{group.label}</option>
                        {group.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {modalError ? (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {modalError}
              </div>
            ) : null}

            {modalLoading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-56 animate-pulse rounded-xl border border-slate-200 bg-slate-100"
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="mb-3 text-sm text-slate-600">
                  Tìm thấy <strong>{filteredModalItems.length}</strong> sản phẩm
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredModalItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                    >
                      <div className="h-28 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                        <Image
                          src={imageSrc(item)}
                          alt={item.name}
                          width={320}
                          height={160}
                          unoptimized
                          className="h-full w-full object-contain p-1 mix-blend-multiply"
                        />
                      </div>

                      <Link
                        href={`/san-pham/${item.slug}`}
                        target="_blank"
                        className="mt-2 line-clamp-2 text-sm font-bold text-slate-900 hover:text-blue-700"
                      >
                        {item.name}
                      </Link>

                      <div className="mt-1 text-[11px] text-slate-500">
                        SKU: {item.sku || 'N/A'}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-600 line-clamp-2">
                        {item.specs || 'Thông số đang cập nhật'}
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-sm font-extrabold text-red-600">
                          {formatPrice(getPrice(item))}
                        </span>
                        {getOriginalPrice(item) > getPrice(item) ? (
                          <span className="text-[11px] text-slate-400 line-through">
                            {formatPrice(getOriginalPrice(item))}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-1 text-xs">
                        {item.stock > 0 ? (
                          <span className="font-semibold text-emerald-700">
                            Còn {item.stock} sản phẩm
                          </span>
                        ) : (
                          <span className="font-semibold text-red-700">Hết hàng</span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => selectProduct(activeSlot, item)}
                        className="mt-3 w-full rounded-lg bg-blue-700 px-3 py-2 text-sm font-bold text-white hover:bg-blue-800"
                      >
                        Chọn
                      </button>
                    </div>
                  ))}
                </div>

                {filteredModalItems.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-5 text-center text-sm text-slate-600">
                    Không có sản phẩm phù hợp bộ lọc hiện tại.
                  </div>
                ) : null}

                {(modalData?.totalPages || 1) > 1 ? (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      disabled={(modalData?.page || 1) <= 1}
                      onClick={() =>
                        setFilters(activeSlot, { page: Math.max(1, (modalData?.page || 1) - 1) })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Trang trước
                    </button>
                    <span className="text-sm text-slate-600">
                      Trang {modalData?.page || 1}/{modalData?.totalPages || 1}
                    </span>
                    <button
                      type="button"
                      disabled={(modalData?.page || 1) >= (modalData?.totalPages || 1)}
                      onClick={() =>
                        setFilters(activeSlot, {
                          page: Math.min(modalData?.totalPages || 1, (modalData?.page || 1) + 1),
                        })
                      }
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Trang sau
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}

      {toast ? (
        <div
          className="fixed right-4 z-[1300]"
          style={{ top: 'calc(var(--site-header-height, 92px) + 12px)' }}
        >
          <div
            className={`rounded-lg border px-4 py-3 text-sm shadow-lg ${
              toast.tone === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : toast.tone === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-blue-200 bg-blue-50 text-blue-700'
            }`}
          >
            {toast.message}
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
